import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function About() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/39732a2546a0e1758010aa22092d6a43.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-blue-900/70"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Growing Stronger <span className="text-yellow-300">Together</span>
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
              Like a tree with deep roots and reaching branches, IRN has grown from a small vision into a thriving network of hope and transformation.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Story</h2>
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  Founded in 2008, Integrated Recovery Network began as a small community initiative with a powerful vision: to break the destructive cycle between the justice system and homelessness that trapped so many in our community.
                </p>
                <p>
                  What started as a grassroots effort has grown into a comprehensive 501(c)3 organization serving thousands of individuals. Our founders, many with lived experience of justice involvement and housing insecurity, understood that real change required more than temporary fixes—it demanded a complete transformation of how we approach recovery and reintegration.
                </p>
                <p>
                  Today, after 18+ years of dedicated service, we continue to plant seeds of hope and watch them grow into thriving, independent lives. Our tree of support has deep roots in evidence-based practices and branches that reach every aspect of recovery.
                </p>
              </div>
              <div className="mt-8">
                <Button variant="primary" size="lg">
                  <i className="ri-timeline-line mr-2"></i>
                  View Our Timeline
                </Button>
              </div>
            </div>
            <div 
              className="rounded-xl shadow-2xl bg-cover bg-center h-96"
              style={{
                backgroundImage: `url('https://static.readdy.ai/image/b9d8b8cf473b691e61500990b41a4db8/9f9adc07f41eeb18cc816d15419cc822.jfif')`,
                backgroundPosition: 'center top'
              }}
            ></div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Like the branches of a tree, our values reach in all directions, supporting every aspect of our work and community.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-500">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-diversity-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Diversity & Inclusion</h3>
              <p className="text-gray-600 leading-relaxed">
                We celebrate the rich diversity of our community and ensure that every individual feels valued, respected, and empowered regardless of their background or identity.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-green-500">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-user-heart-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Lived Experience</h3>
              <p className="text-gray-600 leading-relaxed">
                Our staff's personal experiences with justice involvement and housing insecurity bring authentic understanding and genuine empathy to every interaction.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-500">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Trauma-Informed Care</h3>
              <p className="text-gray-600 leading-relaxed">
                We recognize the impact of trauma and create safe, supportive environments that promote healing and resilience at every step of the journey.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-green-500">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-hand-heart-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Harm Reduction</h3>
              <p className="text-gray-600 leading-relaxed">
                We meet people where they are in their journey, focusing on reducing harm and supporting individual choices without judgment or conditions.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-emerald-500">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-seedling-line text-2xl text-emerald-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Strength-Based</h3>
              <p className="text-gray-600 leading-relaxed">
                We focus on each person's inherent strengths, capabilities, and potential, building upon their existing resources to foster growth and independence.
              </p>
            </div>
            
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow border-l-4 border-green-500">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <i className="ri-community-line text-2xl text-green-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Community Partnership</h3>
              <p className="text-gray-600 leading-relaxed">
                We believe in the power of collaboration and work closely with community partners to create a comprehensive network of support and resources.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Leadership</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the dedicated leaders who guide our mission with wisdom, compassion, and lived experience.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div 
                className="w-32 h-32 rounded-full mx-auto mb-4 bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://static.readdy.ai/image/b9d8b8cf473b691e61500990b41a4db8/3881df4a41bfba240e1e086edf323bff.jfif')`,
                  backgroundPosition: 'center top'
                }}
              ></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Marsha Temple</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                With passionate leadership and deep community understanding, Marsha founded IRN with a vision to break the cycle between the justice system and homelessness.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div 
                className="w-32 h-32 rounded-full mx-auto mb-4 bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://static.readdy.ai/image/b9d8b8cf473b691e61500990b41a4db8/770fddb15fe17fe888c6e776d5f7a2cf.jfif')`,
                  backgroundPosition: 'center top'
                }}
              ></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Marcos Delgadillo</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                A dedicated advocate with lived experience, Marcos leads IRN with expertise in criminal justice reform and comprehensive reentry services.
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
              <div 
                className="w-32 h-32 rounded-full mx-auto mb-4 bg-cover bg-center"
                style={{
                  backgroundImage: `url('https://static.readdy.ai/image/b9d8b8cf473b691e61500990b41a4db8/6c074d47d42ae6d4285f7d378a7d66c2.jfif')`,
                  backgroundPosition: 'center top'
                }}
              ></div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Ruby Villarreal</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                Ruby brings extensive experience in community outreach and program coordination, ensuring our services reach those who need them most effectively.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section 
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage: `url('https://readdy.ai/api/search-image?query=Symbolic%20bridge%20connecting%20two%20landscapes%20with%20tree%20of%20life%20growing%20in%20center%2C%20diverse%20people%20walking%20across%20bridge%20toward%20hope%20and%20opportunity%2C%20golden%20hour%20lighting%2C%20metaphor%20for%20transformation%20and%20connection%2C%20peaceful%20and%20inspiring%20atmosphere%2C%20professional%20artistic%20photography&width=1920&height=800&seq=mission-bridge&orientation=landscape')`
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/80 to-blue-900/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-8">Our Mission</h2>
          <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
            <p className="text-2xl text-white leading-relaxed font-medium">
              "To close the revolving door between the justice system and homelessness by providing housing, case management, employment and mental health therapy."
            </p>
          </div>
          <div className="mt-12">
            <Button variant="secondary" size="lg">
              <i className="ri-arrow-right-line mr-2"></i>
              See Our Impact
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
